import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Send, X, Sparkles, MessageSquare, History, Bookmark, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  role: 'bot' | 'user';
  content: string;
}

const SUGGESTIONS_DEFAULT = [
  'Como cadastro um pagamento?',
  'Validar minha prestação atual',
  'Quais documentos preciso reunir pro fechamento do mês?',
];

// Sugestões dinâmicas por rota (handoff SCREENS.md §9).
function pickSuggestions(pathname: string): string[] {
  if (pathname.startsWith('/prestacao-live')) {
    return [
      'Por que essa fase travou?',
      'O que a automação está fazendo agora?',
      'Quando posso pegar manual?',
    ];
  }
  if (pathname.startsWith('/prestacoes')) {
    return [
      'Como iniciar uma prestação?',
      'O que valida antes de enviar ao portal?',
      'Posso reabrir uma prestação já concluída?',
    ];
  }
  if (pathname.startsWith('/lancamentos/pagamentos')) {
    return [
      'Como cadastrar um pagamento com NF?',
      'Como anexar o comprovante PIX?',
      'O que muda entre "pago" e "conciliado"?',
    ];
  }
  if (pathname.startsWith('/lancamentos/receitas')) {
    return [
      'Como registrar uma receita do convênio?',
      'Quais categorias de receita existem?',
      'Posso importar receitas em massa?',
    ];
  }
  if (pathname.startsWith('/lancamentos')) {
    return [
      'Como categorizar este lançamento?',
      'Buscar pagamentos por fornecedor',
      'Quais campos são obrigatórios?',
    ];
  }
  if (pathname.startsWith('/conciliacao')) {
    return [
      'Por que esse lançamento não bateu com o extrato?',
      'Como conciliar uma diferença de centavos?',
      'Como remover uma conciliação errada?',
    ];
  }
  if (pathname.startsWith('/documentos')) {
    return [
      'Como organizar documentos por tag?',
      'Como buscar dentro do conteúdo do PDF?',
      'Posso anexar um documento direto à prestação?',
    ];
  }
  if (pathname.startsWith('/master')) {
    return [
      'Como impersonar um workspace?',
      'Onde altero a tabela de preços de IA?',
      'Como auditar quem acessou o quê?',
    ];
  }
  return SUGGESTIONS_DEFAULT;
}

const WELCOME_MESSAGES: Message[] = [
  {
    id: 'welcome-1',
    role: 'bot',
    content:
      'Olá! Sou o assistente do GestAKI. Posso explicar fluxos, validar suas prestações e ajudar a interpretar o portal Suzano.',
  },
];

export function IAPanel({ open, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>(WELCOME_MESSAGES);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const suggestions = useMemo(() => pickSuggestions(pathname), [pathname]);

  // Backend ainda não tem créditos Anthropic — modo demo.
  const demoMode = true;

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    setMessages((m) => [...m, userMsg]);
    setInput('');

    setTimeout(() => {
      const botMsg: Message = {
        id: `b-${Date.now()}`,
        role: 'bot',
        content: demoMode
          ? 'Estou em modo de demonstração — o backend de IA ainda não está conectado (faltam créditos Anthropic). Em breve respondo de verdade!'
          : '...',
      };
      setMessages((m) => [...m, botMsg]);
    }, 400);
  }

  if (!open) return null;

  return (
    <>
      {/* Click outside captura */}
      <div
        className="fixed inset-0 z-30 bg-[rgba(20,16,8,0.12)] backdrop-blur-[1px] data-[state=open]:animate-fade-in lg:hidden"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Assistente IA"
        className={cn(
          'fixed top-16 right-[60px] z-40',
          'w-[min(92vw,400px)] h-[calc(100dvh-4rem)]',
          'flex flex-col bg-card border-l border-card-stroke shadow-clay',
          'animate-slide-in-right',
        )}
      >
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-card-stroke px-4 py-3 bg-card-soft">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className={cn(
                'relative grid h-8 w-8 shrink-0 place-items-center rounded-full',
                'bg-[radial-gradient(120%_100%_at_0%_0%,rgba(255,255,255,0.55),transparent_60%),conic-gradient(from_220deg_at_50%_50%,#D5C8E8_0deg,#EDCBB4_140deg,#BFDDC9_260deg,#D5C8E8_360deg)]',
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-2px_4px_rgba(40,30,80,0.18)]',
              )}
              aria-hidden
            >
              <Sparkles className="h-3.5 w-3.5 text-[#2A2240]" strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <p className="font-display text-[14px] font-bold tracking-[-0.01em] text-fg leading-tight">
                Assistente GestAKI
              </p>
              <p className="text-[10px] text-fg-muted font-mono uppercase tracking-[0.10em]">
                Claude Sonnet 4.7
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-2 text-fg-muted hover:text-fg hover:bg-card transition-colors"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </header>

        {/* Sugestões */}
        {messages.length === WELCOME_MESSAGES.length && (
          <div className="border-b border-card-stroke px-4 py-3 bg-card-soft/40">
            <p className="font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted mb-2">
              Sugestões
            </p>
            <div className="flex flex-col gap-1.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className={cn(
                    'text-left text-[13px] text-fg rounded-2 px-3 py-2 border border-card-stroke',
                    'bg-card hover:bg-mint-soft hover:border-mint-deep/40 hover:text-mint-deep',
                    'transition-colors duration-fast',
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mensagens */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                'max-w-[85%] rounded-3 px-3.5 py-2.5 text-[13px] leading-relaxed shadow-1',
                m.role === 'user'
                  ? 'ml-auto bg-[linear-gradient(150deg,var(--mint-softer),var(--mint-soft))] text-on-mint border border-mint-deep/15'
                  : 'mr-auto bg-[linear-gradient(150deg,var(--card),var(--card-soft))] text-fg border border-card-stroke',
              )}
            >
              {m.content}
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="border-t border-card-stroke p-3 bg-card-soft/40">
          {demoMode && (
            <div className="flex items-center gap-2 mb-2 px-2 text-[10px] font-mono text-fg-muted">
              <Lock className="h-3 w-3" strokeWidth={1.8} />
              modo demonstração · backend IA aguardando créditos
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Digite sua pergunta…"
              className={cn(
                'flex-1 h-10 rounded-pill border border-card-stroke bg-card px-4 text-sm text-fg',
                'placeholder:text-fg-dim shadow-pressed',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep',
              )}
            />
            <button
              type="submit"
              disabled={!input.trim()}
              aria-label="Enviar"
              className={cn(
                'grid h-10 w-10 shrink-0 place-items-center rounded-full',
                'bg-[linear-gradient(160deg,#6FAE93,var(--mint-deep))] text-[#FBF7EF]',
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.25),inset_0_-2px_0_rgba(20,40,30,0.18),0_4px_10px_-4px_rgba(40,90,70,0.40)]',
                'hover:brightness-105 transition-all',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100',
              )}
            >
              <Send className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}

export { MessageSquare, History, Bookmark };
